import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import * as lessonActions from 'Actions/lesson';
import { MainAppReducer } from 'Types';
import { Box, Button } from '@mui/material';
import { CustomSwitch } from 'Components';
import Play from 'assets/images/Play.png';
import Pause from 'assets/images/Pause.png';
import Fullscreen from 'Components/Fullscreen';
import { LessonReducer } from 'Reducers/lessonReducer';
import React, { Dispatch, SetStateAction } from 'react';

type FooterContainerProps = {
  setCursorIsVisible: (cursorIsVisible: boolean) => void;
  cursorIsVisible: boolean;
  playButtonActive?: boolean;
  debugShowTierSummary?: boolean; // NEW
  setDebugShowTierSummary?: (v: boolean) => void; // NEW
  freePlay: boolean;
  setFreePlay: Dispatch<SetStateAction<boolean>>;
  mobile: boolean;
  onPlayButtonOverlapChange?: (overlap: number) => void;
};

const FooterContainer = (props: FooterContainerProps) => {
  const dispatch = useDispatch();
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const lesson = useSelector((state: LessonReducer) => state.lessonReducer);
  const playButtonRef = React.useRef<HTMLDivElement>(null);

  const { userData } = data;

  const playLevel = () => {
    dispatch(lessonActions.setLessonPlaying({ lessonPlaying: !lesson.lessonPlaying }));
  };

  const { freePlay, setFreePlay } = props;

  const {
    cursorIsVisible,
    setCursorIsVisible,
    playButtonActive = true,
    debugShowTierSummary = false,
    setDebugShowTierSummary,
    mobile,
    onPlayButtonOverlapChange,
  } = props;

  const hiddenStyles =
    lesson.lessonPlaying && mobile
      ? {
          transform: 'translateY(40px)',
        }
      : {
          transform: 'none',
        };

  const playButtonHiddenStyles =
    lesson.lessonPlaying && mobile
      ? {
          transform: 'translateY(50px)',
        }
      : {
          transform: 'none',
        };

  // Calculate play button overlap with canvas
  React.useEffect(() => {
    if (!onPlayButtonOverlapChange || mobile) {
      return;
    }

    const calculateOverlap = () => {
      const playButton = playButtonRef.current;
      if (!playButton) return;

      const buttonRect = playButton.getBoundingClientRect();

      // Find the main app container (canvas area)
      const mainAppContainer = document.querySelector('.main-app-container-styles');
      if (!mainAppContainer) return;

      const containerRect = mainAppContainer.getBoundingClientRect();

      // Calculate how much the button overlaps into the canvas from the bottom
      // The button is positioned absolutely at the bottom right
      // We need to check how much of the button's top portion overlaps into the canvas
      const buttonTop = buttonRect.top;
      const containerBottom = containerRect.bottom;

      // If button top is above container bottom, it's overlapping into the canvas
      const overlap = Math.max(0, containerBottom - buttonTop);

      // Add 10px buffer as requested
      const overlapWithBuffer = overlap > 0 ? overlap + 10 : 0;

      onPlayButtonOverlapChange(overlapWithBuffer);
    };

    // Calculate on mount and when relevant styles change
    calculateOverlap();

    // Recalculate on resize
    window.addEventListener('resize', calculateOverlap);

    return () => {
      window.removeEventListener('resize', calculateOverlap);
    };
  }, [onPlayButtonOverlapChange, mobile, lesson.lessonPlaying]);

  return (
    <Box
      sx={{
        //opacity: 0,
        display: 'flex',
        backgroundColor: '#050B13',
        height: mobile ? '40px' : '100px',
        justifyContent: 'space-between',
        position: mobile ? 'absolute' : 'relative',
        bottom: mobile ? 0 : 'auto',
        width: '100%',
        flexShrink: 0, // Don't shrink - keep footer at fixed height
        transition: 'transform 1s ease-in-out',
        ...hiddenStyles,
      }}
    >
      <Box
        sx={{
          fontStyle: 'normal',
          fontWeight: '400',
          fontSize: '16px',
          letterSpacing: '0.01em',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          paddingLeft: mobile ? 'auto' : '30px',
        }}
      >
        {!mobile && <Fullscreen show_label={false} />}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyItems: 'center' }}>
          <CustomSwitch defaultChecked onChange={() => setCursorIsVisible(!cursorIsVisible)} />
          <Box>Cursor</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* I think we're kind of using the state backwards here? I.e. the off UI is the on UI? */}
          <CustomSwitch
            checked={userData?.audio_on ?? true}
            onChange={() => {
              dispatch(appActions.setAudioOn({ audioOn: !(userData?.audio_on ?? true) }));
            }}
          />
          <Box>Keyboard Sound</Box>
          {process.env.REACT_APP_NODE_ENV === 'local' && setDebugShowTierSummary && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
              <CustomSwitch
                checked={!!debugShowTierSummary}
                onChange={() => setDebugShowTierSummary(!debugShowTierSummary)}
              />
              <Box>Tier Summary</Box>
            </Box>
          )}
        </Box>
      </Box>
      {mobile && <PlayModeSelector freePlay={freePlay} setFreePlay={setFreePlay} mobile={mobile} />}
      <Button onClick={() => playLevel()} disabled={!playButtonActive}>
        <Box
          ref={playButtonRef}
          sx={{ ...playButtonStyles(mobile), ...playButtonHiddenStyles }}
          id="playButton"
        >
          {lesson.lessonPlaying ? (
            <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={Pause} />
          ) : (
            <img style={{ maxWidth: '100%', maxHeight: '100%', margin: '0px' }} src={Play} />
          )}
        </Box>
      </Button>
    </Box>
  );
};

type PlayModeSelectorProps = {
  freePlay: boolean;
  setFreePlay: Dispatch<SetStateAction<boolean>>;
  mobile: boolean;
};

export function PlayModeSelector({ freePlay, setFreePlay, mobile }: PlayModeSelectorProps) {
  const freePlaySelectedStyles = {
    // boxShadow: !freePlay ? '0 0 8px rgba(45, 109, 163, 0.8)': 'none'
    transform: 'scale(1.05, 1)',
    transition: 'transform 0.2s',
    overflow: 'hidden',
    '& p': {
      fontWeight: 'bold',
    },
    height: '29px',
  };
  return (
    <Button
      sx={{
        display: 'flex',
        alignItems: 'end',
        alignContent: 'center',
        justifyContent: 'center',
        height: mobile ? 'auto' : '100%',
        padding: mobile ? 'auto' : 0,
      }}
    >
      <Box
        sx={{
          height: '30px',
          width: '240px',
          background: '#414753',
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center',
          borderRadius: mobile
            ? '0 0 20px 20px' /* top-left, top-right, bottom-right, bottom-left */
            : '20px 20px 0 0',
          '& p': {
            color: '#fff',
            margin: '5px',
            padding: 0,
          },
          //zIndex: 1,
          position: mobile ? 'absolute' : 'auto',
          top: 0,
          // margin: '10px'
        }}
      >
        <Box
          sx={{
            // height: '30px',
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            width: '120px',
            background: !freePlay ? '#3C54AF' : '#424652',
            borderRadius: mobile
              ? '0 0 0 20px' /* top-left, top-right, bottom-right, bottom-left */
              : '20px 0 0 0',
            borderRightStyle: 'solid',
            borderColor: '#414753',
            '&:hover': {
              cursor: 'pointer',
            },
            ...(!freePlay ? freePlaySelectedStyles : {}),
          }}
          onClick={() => setFreePlay(false)}
        >
          <p>Game</p>
        </Box>
        <Box
          sx={{
            // height: '30px',
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            width: '120px',
            background: freePlay ? '#3C54AF' : '#424652',
            borderRadius: mobile
              ? '0 0 20px 0' /* top-left, top-right, bottom-right, bottom-left */
              : '0 20px 0 0',
            borderLeftStyle: 'solid',
            borderColor: '#414753',
            // boxShadow: freePlay ? '0 0 8px rgba(45, 109, 163, 0.8)': 'none'
            transform: freePlay ? 'scale(1.05, 1)' : 'none' /* Slightly bigger */,
            transition: freePlay ? 'transform 0.2s' : 'none',
            '&:hover': {
              cursor: 'pointer',
            },
            ...(freePlay ? freePlaySelectedStyles : {}),
          }}
          onClick={() => setFreePlay(true)}
        >
          <p>Free Play</p>
        </Box>
      </Box>
    </Button>
  );
}

export default FooterContainer;

const playButtonStyles = (mobile: boolean) => ({
  position: 'absolute',
  right: '40px',
  bottom: mobile ? '10px' : '30px',
  width: mobile ? '80px' : '105px',
  height: mobile ? '80px' : '105px',
  padding: '0px',
  margin: '0px',
  borderRadius: '50%',
  transition: 'transform 1s',
  '&:hover': {
    background: 'rgba(212,134,108,1.0)',
    cursor: 'pointer',
    boxShadow: '0px 0px 5px 5px rgb(212,134,108,.5)',
  },
});
